import React, { useState, useEffect, useRef } from "react";
import { RiUserAddFill } from "react-icons/ri";
import { TbListDetails } from "react-icons/tb";
import { IoInformationCircle } from "react-icons/io5";
import { MdAdd, MdDelete, MdClose, MdCloudUpload } from "react-icons/md";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Alert } from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import axios from '../../Authservices/axiosInstance';
import { canSeeSalary, canChangeRole } from "../../utils/permissions";
import "./add-emp.css";

const AddEmp = () => {
  // ===== Permissions =====
  const showSalary = canSeeSalary();
  const showRole = canChangeRole();

  const [role, setRole] = useState(null);
  const [department, setDepartment] = useState(null);
  const [gender, setGender] = useState(null);
  const [country, setCountry] = useState(null);
  const [joinDate, setJoinDate] = useState("");
  const [birthday, setBirthday] = useState("");
  const [status, setStatus] = useState(null);
  const [maritalstatus, setMaritalStatus] = useState(null);
  const [countries, setCountries] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: "",
    startDate: "",
    expiryDate: "",
    files: [],
    fileNames: [],
    type: ""
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const documentTypeOptions = [
    { value: "ID Documents", label: "ID Documents" },
    { value: "Company Data Protection", label: "Company Data Protection" },
    { value: "Handbook and Employment", label: "Handbook and Employment" },
    { value: "Health and Safety", label: "Health and Safety" },
    { value: "Additional Document", label: "Additional Document" }
  ];

  const shouldHideDates = (type) => {
    return type && !["ID Documents", "Additional Document"].includes(type);
  };

  useEffect(() => {
    fetch("https://restcountries.com/v3.1/all?fields=cca2,name")
      .then((response) => response.json())
      .then((data) => {
        const countryOptions = data
          .filter(c => c.cca2 && c.name?.common)
          .map((country) => ({ value: country.cca2, label: country.name.common }));
        setCountries(countryOptions);
      })
      .catch((error) => console.error("Error fetching countries:", error));
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get("https://backend.ems.uk.hiradentallab.com/api/departments");
        const departmentOptions = response.data.map((department) => ({ value: department._id, label: department.name }));
        setDepartmentOptions(departmentOptions);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  const formatForBackend = (dateString) => {
    if (!dateString) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) return dateString;
    const parts = dateString.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  const openDocumentModal = () => {
    setNewDocument({
      name: "",
      startDate: "",
      expiryDate: "",
      files: [],
      fileNames: [],
      type: ""
    });
    setShowDocumentModal(true);
  };

  const closeDocumentModal = () => setShowDocumentModal(false);

  const handleDocumentChange = (e) => {
    const { name, value } = e.target;
    setNewDocument(prev => ({ ...prev, [name]: value }));
  };

  const handleDocumentTypeChange = (selectedOption) => {
    setNewDocument(prev => ({ ...prev, type: selectedOption?.value || "" }));
  };

  const handleDocumentFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setNewDocument(prev => ({
        ...prev,
        files: [...prev.files, ...newFiles],
        fileNames: [...prev.fileNames, ...newFiles.map(f => f.name)]
      }));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFileFromNewDocument = (index) => {
    setNewDocument(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      fileNames: prev.fileNames.filter((_, i) => i !== index)
    }));
  };

  const addDocument = () => {
    if (!newDocument.name || newDocument.files.length === 0 || !newDocument.type) {
      alert("Document Name, Type, and at least one file are required!");
      return;
    }

    const newDocs = newDocument.files.map(file => ({
      name: newDocument.name,
      startDate: newDocument.startDate,
      expiryDate: newDocument.expiryDate,
      file: file,
      fileName: file.name,
      type: newDocument.type
    }));

    setDocuments(prev => [...prev, ...newDocs]);
    closeDocumentModal();
  };

  const groupDocuments = (docs) => {
    const groups = {};
    docs.forEach(doc => {
      const key = `${doc.name}|${doc.type}|${doc.startDate}|${doc.expiryDate}`;
      if (!groups[key]) {
        groups[key] = {
          name: doc.name,
          type: doc.type,
          startDate: doc.startDate,
          expiryDate: doc.expiryDate,
          files: []
        };
      }
      groups[key].files.push({ fileName: doc.fileName, file: doc.file });
    });
    return Object.values(groups);
  };

  const removeDocumentGroup = (group) => {
    setDocuments(prev => prev.filter(doc =>
      !(doc.name === group.name &&
        doc.type === group.type &&
        doc.startDate === group.startDate &&
        doc.expiryDate === group.expiryDate)
    ));
  };

  const handleProfilePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfilePhoto(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData();

    // Read a text input by its name attribute
    const val = (fieldName) => (form.elements.namedItem(fieldName)?.value || "").trim();

    // Only append a field if it actually has a value (keeps optional fields out of the request)
    const appendIfFilled = (key, value) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        formData.append(key, String(value).trim());
      }
    };

    // ===== REQUIRED =====
    formData.append("name", val("name"));
    formData.append("username", val("username"));
    formData.append("password", form.elements.namedItem("password").value);
    formData.append("pin", val("pin"));

    // ===== OPTIONAL =====
    // Staff cannot choose a role – backend forces "Employee" for staff anyway
    if (showRole) {
      formData.append("role", role?.value || "Employee");
    }
    formData.append("status", status?.value || "active");

    appendIfFilled("email", val("email"));
    appendIfFilled("department", department?.value);
    appendIfFilled("joinDate", joinDate);
    appendIfFilled("birthday", birthday);
    appendIfFilled("gender", gender?.value);
    appendIfFilled("maritalstatus", maritalstatus?.value);
    appendIfFilled("phone", val("phone"));
    appendIfFilled("city", val("city"));
    appendIfFilled("country", country?.value);
    appendIfFilled("postalCode", val("postalCode"));
    appendIfFilled("address", val("address"));
    appendIfFilled("workExperience", val("workExperience"));
    appendIfFilled("workDescription", val("workDescription"));

    // Salary fields only for users allowed to see them
    if (showSalary) {
      appendIfFilled("salary", val("salary").replace("£", "").trim());
      appendIfFilled("hourlyRate", val("hourlyRate").replace("£", "").trim());
    }

    appendIfFilled("jobTitle", val("jobTitle"));
    appendIfFilled("nextOfKinName", val("nextOfKinName"));
    appendIfFilled("nextOfKinRelationship", val("nextOfKinRelationship"));
    appendIfFilled("nextOfKinPhone", val("nextOfKinPhone"));
    appendIfFilled("nextOfKinEmail", val("nextOfKinEmail"));
    appendIfFilled("nextOfKinAddress", val("nextOfKinAddress"));

    if (profilePhoto) formData.append("profilePhoto", profilePhoto);

    const documentsMetadata = documents.map(doc => ({
      name: doc.name,
      startDate: formatForBackend(doc.startDate),
      expiryDate: formatForBackend(doc.expiryDate),
      type: doc.type || "Additional Document"
    }));
    formData.append("documentsMetadata", JSON.stringify(documentsMetadata));
    documents.forEach((doc, index) => {
      if (doc.file) {
        formData.append(`documentFiles[${index}]`, doc.file);
      }
    });

    try {
      await axios.post("https://backend.ems.uk.hiradentallab.com/api/user/create", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setAlertMessage("Employee added successfully!");
      setAlertVariant("success");
      setShowAlert(true);
      setTimeout(() => navigate("/emp-info"), 3000);
    } catch (error) {
      console.error("Error adding employee:", error.response?.data || error);
      setAlertMessage(error.response?.data?.message || "Error adding employee. Please try again.");
      setAlertVariant("danger");
      setShowAlert(true);
    }
  };

  const roleOptions = [
    { value: "Employee", label: "Employee" },
    { value: "Admin", label: "Admin" },
    { value: "HR", label: "HR" },
    { value: "Accounts", label: "Accounts" }
  ];

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const maritalstatusOptions = [
    { value: "Single", label: "Single" },
    { value: "Married", label: "Married" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const groupedDocuments = groupDocuments(documents);

  return (
    <div className="container-fluid">
      <div className="emp-information-container">
        <h1 style={{ color: "#0353a4", fontSize: "2rem", marginTop: "65px" }}><RiUserAddFill /> Add Employee</h1>
        <hr className="section-divider" />
        <h1 style={{ color: "#0353a4", textAlign: "left", fontSize: "2rem" }}><TbListDetails /> Login Details</h1>
        <hr className="section-divider" />
      </div>
      <Form onSubmit={handleSubmit} className="add-emp-form">
        <div className="add-emp-container">
          {showAlert && <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>{alertMessage}</Alert>}
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={5} controlId="formGridName">
              <Form.Label>Name<span className="required">*</span>:</Form.Label>
              <Form.Control type="text" name="name" required placeholder="Enter Your Name" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridPassword">
              <Form.Label>Password<span className="required">*</span>:</Form.Label>
              <Form.Control type="password" name="password" required placeholder="Enter Password" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridPin">
              <Form.Label>PIN<span className="required">*</span>:</Form.Label>
              <Form.Control type="password" name="pin" required placeholder="Enter 4-6 digit PIN" maxLength={6} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridUserName">
              <Form.Label>Username <span className="required">*</span>:</Form.Label>
              <Form.Control type="text" name="username" required placeholder="Enter Your Username" />
            </Form.Group>
            {showRole && (
              <Form.Group as={Col} xs={12} md={5} controlId="formGridRole">
                <Form.Label>Role:</Form.Label>
                <Select options={roleOptions} value={role} onChange={setRole} placeholder="--Select Role (default: Employee)--" />
              </Form.Group>
            )}
          </Row>
        </div>

        <div>
          <h1 style={{ color: "#0353a4", textAlign: "left", fontSize: "2rem", marginTop: "30px" }}><IoInformationCircle /> Employee Information <small style={{ fontSize: "1rem", color: "#6c757d" }}>(optional – can be added later)</small></h1>
          <hr className="section-divider" />
        </div>
        <div className="add-emp-container">
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={5} controlId="formGridDepartment">
              <Form.Label>Department</Form.Label>
              <Select options={departmentOptions} value={department} onChange={setDepartment} placeholder="--Department--" isClearable />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridJobTitle">
              <Form.Label>Job Title:</Form.Label>
              <Form.Control type="text" name="jobTitle" placeholder="Enter Your Job Title" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5}>
              <Form.Label>Join Date:</Form.Label>
              <Form.Control type="date" value={joinDate} onChange={(e) => setJoinDate(e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridBirthday">
              <Form.Label>Date Of Birth:</Form.Label>
              <Form.Control type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridGender">
              <Form.Label>Gender</Form.Label>
              <Select options={genderOptions} value={gender} onChange={setGender} placeholder="--Select Gender--" isClearable />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridStatus">
              <Form.Label>Status</Form.Label>
              <Select options={statusOptions} value={status} onChange={setStatus} placeholder="--Select Status (default: Active)--" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridMaritalStatus">
              <Form.Label>Marital Status</Form.Label>
              <Select options={maritalstatusOptions} value={maritalstatus} onChange={setMaritalStatus} placeholder="--Select Marital Status--" isClearable />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridPhone">
              <Form.Label>Phone:</Form.Label>
              <Form.Control type="tel" name="phone" placeholder="Enter Your Phone Number" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridCountry">
              <Form.Label>Country:</Form.Label>
              <Select options={countries} value={country} onChange={setCountry} placeholder="--Select Country--" isClearable />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridCity">
              <Form.Label>City:</Form.Label>
              <Form.Control type="text" name="city" placeholder="Enter Your City" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridPostalCode">
              <Form.Label>Postal Code:</Form.Label>
              <Form.Control type="text" name="postalCode" placeholder="Enter Your Postal Code" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridAddress">
              <Form.Label>Address:</Form.Label>
              <Form.Control type="text" name="address" placeholder="Enter Your Address" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridExperience">
              <Form.Label>Experience:</Form.Label>
              <Form.Control type="text" name="workExperience" placeholder="Enter Your Experience" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={12} controlId="formGridWorkDescription">
              <Form.Label>Work Description:</Form.Label>
              <Form.Control as="textarea" rows={3} name="workDescription" placeholder="Describe Your Work" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridEmail">
              <Form.Label>Email:</Form.Label>
              <Form.Control type="email" name="email" placeholder="Enter Your Email" />
            </Form.Group>

            {/* ===== Salary + Hourly Rate (hidden for staff) ===== */}
            {showSalary && (
              <>
                <Form.Group as={Col} xs={12} md={5} controlId="formGridSalary">
                  <Form.Label>Salary (£):</Form.Label>
                  <Form.Control type="text" name="salary" placeholder="£ Enter Monthly Salary" />
                </Form.Group>

                <Form.Group as={Col} xs={12} md={5} controlId="formGridHourlyRate">
                  <Form.Label>Hourly Rate (£):</Form.Label>
                  <Form.Control
                    type="text"
                    name="hourlyRate"
                    placeholder="£ e.g. 12.71"
                  />
                </Form.Group>
              </>
            )}
          </Row>
        </div>

        <div>
          <h1 style={{ color: "#0353a4", textAlign: "left", fontSize: "2rem", marginTop: "30px" }}>Next of Kin Information</h1>
          <hr className="section-divider" />
        </div>
        <div className="add-emp-container">
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={5} controlId="formGridNextOfKinName">
              <Form.Label>Next of Kin Name:</Form.Label>
              <Form.Control type="text" name="nextOfKinName" placeholder="Enter Next of Kin Name" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridNextOfKinRelationship">
              <Form.Label>Relationship:</Form.Label>
              <Form.Control type="text" name="nextOfKinRelationship" placeholder="e.g., Spouse, Parent, Sibling" />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={5} controlId="formGridNextOfKinPhone">
              <Form.Label>Next of Kin Phone:</Form.Label>
              <Form.Control type="tel" name="nextOfKinPhone" placeholder="Enter Next of Kin Phone Number" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={5} controlId="formGridNextOfKinEmail">
              <Form.Label>Next of Kin Email:</Form.Label>
              <Form.Control type="email" name="nextOfKinEmail" placeholder="Enter Next of Kin Email" />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={12} controlId="formGridNextOfKinAddress">
              <Form.Label>Next of Kin Address:</Form.Label>
              <Form.Control type="text" name="nextOfKinAddress" placeholder="Enter Next of Kin Address" />
            </Form.Group>
          </Row>
        </div>

        {/* ========== DOCUMENTS SECTION ========== */}
        <div>
          <h1 style={{ color: "#0353a4", textAlign: "left", fontSize: "2rem", marginTop: "30px" }}>📄 Documents</h1>
          <hr className="section-divider" />
        </div>
        <div className="add-emp-container">
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6}>
              <Form.Label>Profile Photo:</Form.Label>
              <Form.Control type="file" accept=".png,.jpg,.jpeg" onChange={handleProfilePhotoChange} />
            </Form.Group>
          </Row>

          <Row className="mb-2">
            <Col>
              <h5 style={{ color: "#0353a4" }}>Add Documents</h5>
            </Col>
          </Row>

          <Row className="mb-4">
            <Form.Group as={Col} xs={12} md={8} lg={6}>
              <Button
                variant="primary"
                size="lg"
                onClick={openDocumentModal}
                className="w-100 py-2"
                style={{ fontSize: "1.1rem" }}
              >
                <MdAdd size={20} className="me-1" /> Upload Document
              </Button>
            </Form.Group>
          </Row>

          {groupedDocuments.length > 0 && (
            <div className="mt-4">
              <h5>Added Documents:</h5>
              {groupedDocuments.map((group, idx) => (
                <div key={idx} className="border rounded mb-3 p-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{group.type} - {group.name}</strong>
                      {!shouldHideDates(group.type) && (
                        <div className="text-muted small">
                          Start: {group.startDate || "N/A"} | Expiry: {group.expiryDate || "N/A"}
                        </div>
                      )}
                      <div className="mt-2">
                        <small className="text-secondary">Files:</small>
                        <ul className="mb-0 ps-3">
                          {group.files.map((file, i) => (
                            <li key={i}>{file.fileName}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeDocumentGroup(group)}
                    >
                      <MdDelete /> Delete All
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button variant="primary" type="submit" className="mt-4">Add Employee</Button>
      </Form>

      {/* ========== DOCUMENT MODAL ========== */}
      <Modal show={showDocumentModal} onHide={closeDocumentModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Document Type <span className="required">*</span></Form.Label>
              <Select
                options={documentTypeOptions}
                value={documentTypeOptions.find(opt => opt.value === newDocument.type) || null}
                onChange={handleDocumentTypeChange}
                placeholder="-- Select Document Type --"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Document Name <span className="required">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newDocument.name}
                onChange={handleDocumentChange}
                placeholder="Enter Document Name"
                required
              />
            </Form.Group>

            {!shouldHideDates(newDocument.type) && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={newDocument.startDate}
                      onChange={handleDocumentChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expiry Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="expiryDate"
                      value={newDocument.expiryDate}
                      onChange={handleDocumentChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Files <span className="required">*</span></Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleDocumentFileChange}
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
              />
              <Button
                variant="primary"
                onClick={() => fileInputRef.current?.click()}
                className="mb-2"
              >
                <MdCloudUpload className="me-1" /> Choose Files
              </Button>

              {newDocument.fileNames.length > 0 && (
                <>
                  <div className="border rounded p-2 mb-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                    {newDocument.fileNames.map((name, idx) => (
                      <div key={idx} className="d-flex justify-content-between align-items-center py-1">
                        <small className="text-truncate" style={{ maxWidth: "80%" }}>{name}</small>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => removeFileFromNewDocument(idx)}
                        >
                          <MdClose />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <MdAdd /> Add More Files
                  </Button>
                </>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDocumentModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={addDocument}>
            Add Document(s)
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddEmp;